import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { getApiErrorMessage } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/i18n/useI18n'
import { useCheckoutCartStore, useUserStore } from '@/store'

import { login } from './api/authApi'
import { resolveHomeDestination } from './utils/resolveHomeDestination'
import { useThemeEffect } from '@/features/settings/theme'

function createLoginSchema(t: (key: string) => string) {
  return z.object({
    usernameOrEmail: z.string().trim().min(1, t('auth.login.errors.usernameRequired')),
    password: z.string().min(1, t('auth.login.errors.passwordRequired')),
  })
}

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>

function destinationFromState(state: unknown): string | null {
  if (
    typeof state === 'object' &&
    state !== null &&
    'from' in state &&
    typeof state.from === 'string' &&
    state.from.startsWith('/')
  ) {
    return state.from
  }
  return null
}

export function LoginPage() {
  const { t } = useI18n()
  useThemeEffect()
  const authStatus = useUserStore((state) => state.authStatus)
  const currentUser = useUserStore((state) => state.currentUser)
  const setAuthenticated = useUserStore((state) => state.setAuthenticated)
  const clearSale = useCheckoutCartStore((state) => state.clearSale)
  const navigate = useNavigate()
  const location = useLocation()
  const loginSchema = createLoginSchema(t as unknown as (key: string) => string)
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { usernameOrEmail: '', password: '' },
  })
  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: (session) => {
      clearSale()
      setAuthenticated(session.user, session.accessToken)
      navigate(
        destinationFromState(location.state) ?? resolveHomeDestination(session.user),
        { replace: true },
      )
    },
  })

  if (authStatus === 'authenticated') {
    return <Navigate to={resolveHomeDestination(currentUser)} replace />
  }

  if (authStatus === 'initializing') {
    return (
      <div className="grid min-h-dvh place-items-center bg-background p-6">
        <p className="text-sm text-muted-foreground">{t('auth.restoringSession')}</p>
      </div>
    )
  }

  const errorMessage = loginMutation.error
    ? getApiErrorMessage(loginMutation.error, t('auth.login.errors.failed'))
    : null

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t('auth.login.title')}</CardTitle>
          <CardDescription>
            {t('auth.login.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-5"
            onSubmit={form.handleSubmit((values) => loginMutation.mutate(values))}
          >
            <div className="space-y-2">
              <Label htmlFor="usernameOrEmail">{t('auth.login.usernameLabel')}</Label>
              <Input
                id="usernameOrEmail"
                autoComplete="username"
                aria-invalid={Boolean(form.formState.errors.usernameOrEmail)}
                {...form.register('usernameOrEmail')}
              />
              {form.formState.errors.usernameOrEmail && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.usernameOrEmail.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.login.passwordLabel')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={Boolean(form.formState.errors.password)}
                {...form.register('password')}
              />
              {form.formState.errors.password && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>
            {errorMessage && (
              <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {errorMessage}
              </p>
            )}
            <Button className="w-full" type="submit" disabled={loginMutation.isPending}>
              {loginMutation.isPending ? t('auth.login.loading') : t('auth.login.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  )
}
